import { useEffect } from 'react';
import HeroSection from '../../components/home/HeroSection';
import StatsSection from '../../components/home/StatsSection';
import ServicesSection from '../../components/home/ServicesSection';
import ServiceAreasSection from '../../components/home/ServiceAreasSection';
import ProcessSection from '../../components/home/ProcessSection';
import TestimonialsSection from '../../components/home/TestimonialsSection';
import CTASection from '../../components/home/CTASection';
import Section from '../../components/base/Section';

export default function HomePage() {
  useEffect(() => {
    // SEO Meta Tags
    document.title = 'Granollers Vaciado de Pisos y Limpieza Extrema | JUSTCLEAN Vallès Oriental';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Vaciado de pisos en Granollers y Vallès Oriental. Servicio profesional de limpieza extrema, síndrome de Diógenes y retirada de muebles. Presupuesto gratis en 24h. Discreto y eficiente. ☎️ 602 36 59 96');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'vaciado pisos Granollers, limpieza extrema Vallès Oriental, síndrome Diógenes, retirada muebles, vaciado locales');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://granollers.justclean.es'}/`);
    }

    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    }

    // Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${import.meta.env.VITE_SITE_URL || 'https://granollers.justclean.es'}/#organization`,
      "name": "JUSTCLEAN GRANOLLERS",
      "image": `${import.meta.env.VITE_SITE_URL || 'https://granollers.justclean.es'}/logo.png`,
      "url": import.meta.env.VITE_SITE_URL || 'https://granollers.justclean.es',
      "telephone": "+34602365996",
      "email": "info@granollers.justclean.es",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Carrer de Girona, 45",
        "addressLocality": "Granollers",
        "addressRegion": "Barcelona",
        "postalCode": "08402",
        "addressCountry": "ES"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "41.6077",
        "longitude": "2.2874"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday"
        ],
        "opens": "00:00",
        "closes": "23:59"
      },
      "priceRange": "€€",
      "areaServed": [
        {
          "@type": "City",
          "name": "Granollers"
        },
        {
          "@type": "City",
          "name": "Canovelles"
        },
        {
          "@type": "City",
          "name": "Les Franqueses del Vallès"
        },
        {
          "@type": "City",
          "name": "Parets del Vallès"
        },
        {
          "@type": "City",
          "name": "Montmeló"
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Servicios de Vaciado y Limpieza",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Vaciado de Pisos",
              "description": "Servicio completo de vaciado de pisos y casas en Granollers"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Limpieza Síndrome de Diógenes",
              "description": "Limpieza especializada en casos de síndrome de Diógenes"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Vaciado de Locales",
              "description": "Vaciado de locales comerciales y oficinas"
            }
          }
        ]
      },
      "sameAs": [
        "https://www.facebook.com/justcleangranollers",
        "https://www.instagram.com/justcleangranollers"
      ]
    });
    document.head.appendChild(schemaScript);

    return () => {
      if (schemaScript.parentNode) {
        schemaScript.parentNode.removeChild(schemaScript);
      }
    };
  }, []);

  return (
    <main>
      <HeroSection />
      <StatsSection />
      <ServicesSection />
      <ServiceAreasSection />
      <ProcessSection />
      <TestimonialsSection />
      
      {/* Sección de Enlaces Internos para SEO */}
      <Section background="white" padding="lg">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-secondary mb-6">
            Más información sobre JUSTCLEAN Granollers
          </h2>
          <p className="text-lg text-tertiary leading-relaxed mb-8">
            Descubre todo lo que necesitas saber sobre nuestros servicios de vaciado de pisos y limpieza extrema en Granollers y Vallès Oriental.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a href="/empresa" className="bg-light rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 cursor-pointer group">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                <i className="ri-building-line text-3xl text-primary"></i>
              </div>
              <h3 className="text-xl font-bold text-secondary mb-2 group-hover:text-primary transition-colors duration-300">Nuestra Empresa</h3>
              <p className="text-sm text-tertiary">Conoce nuestra historia, valores y experiencia en Granollers</p>
            </a>
            
            <a href="/servicios" className="bg-light rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 cursor-pointer group">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                <i className="ri-service-line text-3xl text-primary"></i>
              </div>
              <h3 className="text-xl font-bold text-secondary mb-2 group-hover:text-primary transition-colors duration-300">Todos los Servicios</h3>
              <p className="text-sm text-tertiary">Vaciado, limpieza Diógenes, pintura y más servicios</p>
            </a>
            
            <a href="/faq" className="bg-light rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 cursor-pointer group">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                <i className="ri-question-answer-line text-3xl text-primary"></i>
              </div>
              <h3 className="text-xl font-bold text-secondary mb-2 group-hover:text-primary transition-colors duration-300">Preguntas Frecuentes</h3>
              <p className="text-sm text-tertiary">Resuelve todas tus dudas sobre nuestros servicios</p>
            </a>
          </div>
        </div>
      </Section>
      
      <CTASection />
    </main>
  );
}
